import { yupResolver } from '@hookform/resolvers/yup';
import DatePicker from '@mui/lab/DatePicker';
import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { date, object, string } from 'yup';
import IconSuccess from '../../../../assets/icon-success.svg';
import DetailsCard from '../../../../components/DetailsCard/DetailsCard';
import FormatPhoneInput from '../../../../components/FormatNumber/FormatPhoneInput';
import DefaultModal from '../../../../components/Modals/DefaultModal';
import PageTitle from '../../../../components/PageTitle/PageTitle';
import { dashBorder } from '../../../../constants';
import { Countries } from '../../../../enums/countries';
import { TalentStatus } from '../../../../enums/talentStatus';
import { Company } from '../../../../models/company';
import { Talent } from '../../../../models/talent';
import {
  ADMINISTRATION_TALENT,
  ADMINISTRATION_TALENT_CREATE
} from '../../../../router/constants';
import { companyServices } from '../../../../services/companyServices';
import { talentService } from '../../../../services/talentService';
import { removeEmptyObjectFields } from '../../../../utils/helpers';

const talentSchema = yup.object({
  firstName: string().default('').required('First Name is required'),
  lastName: string().default('').required('Last Name is required'),
  address: string().default('').required('Address is required'),
  address2: string().default(''),
  city: string().default('').required('City is required'),
  zipCode: string().default('').required('Zip Code is required'),
  country: string().default(Countries.USA).required('Country is required'),
  state: string().default('').required('State is required'),
  email: string()
    .default('')
    .email('Primary Email must be a valid email')
    .required('Primary Email is required'),
  secondaryEmail: string().default(''),
  phoneNumber: string().default(''),
  altPhone: string().default(''),
  birthDate: date().nullable().default(null),
  accountType: string().default('').required('Account Type is required'),
  status: string().default('').required('Status is required'),
  company: object()
    .default(null)
    .required('Company is required')
    .typeError('Company is required'),
  awPayrollId: string().default('').required('AW Payroll ID is required')
});

type CreateTalentForm = yup.InferType<typeof talentSchema>;

const defaultValues = talentSchema.getDefault();

const CreateTalent = (): JSX.Element => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [successModal, setSuccessModal] = useState<boolean>(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<CreateTalentForm>({
    defaultValues,
    resolver: yupResolver(talentSchema)
  });

  useEffect(() => {
    if (id) {
      talentService.getTalentById(Number(id)).then((response) => {
        const data = removeEmptyObjectFields(response.data);
        reset(data as unknown as CreateTalentForm);
      });
    }
  }, [id]);

  useEffect(() => {
    companyServices.getCompaniesAutocomplete().then((response) => {
      setCompanies(response.data);
    });
  }, []);

  const createTalent = (talent: Talent) => {
    talentService.createTalent(talent).then(() => {
      setSuccessModal(true);
    });
  };

  const updateTalent = (talent: Talent) => {
    talentService.updateTalent(talent).then(() => {
      setSuccessModal(true);
    });
  };

  const closeSuccessModal = () => {
    reset(defaultValues);
    if (id) {
      navigate(ADMINISTRATION_TALENT_CREATE);
    }
    setSuccessModal(false);
  };

  const onSubmit = (data: CreateTalentForm) => {
    const talent = data as unknown as Talent;
    id ? updateTalent(talent) : createTalent(talent);
  };

  return (
    <>
      <PageTitle>{id ? 'Edit Talent' : 'Add New Talent'}</PageTitle>
      <DetailsCard title={id ? 'Edit Talent' : 'New Talent'}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ ...dashBorder, mb: 3 }}>
            <Typography variant="h6" color="grey.900" mb={2}>
              Contact Information
            </Typography>
            <Grid container item spacing={10}>
              <Grid container item xs gap={3}>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="First Name *"
                      {...field}
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Last Name *"
                      {...field}
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                    />
                  )}
                />
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Address *"
                      {...field}
                      error={!!errors.address}
                      helperText={errors.address?.message}
                    />
                  )}
                />
                <Controller
                  name="address2"
                  control={control}
                  render={({ field }) => (
                    <TextField label="Address 2" {...field} />
                  )}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="City *"
                        {...field}
                        error={!!errors.city}
                        helperText={errors.city?.message}
                      />
                    )}
                  />
                  <Controller
                    name="zipCode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Zip Code *"
                        {...field}
                        error={!!errors.zipCode}
                        helperText={errors.zipCode?.message}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                  <Controller
                    name="country"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        id="select"
                        select
                        label="Country *"
                        {...field}
                        error={!!errors.country}
                        helperText={errors.country?.message}
                      >
                        {Object.keys(Countries).map((country) => (
                          <MenuItem key={country} value={country}>
                            {country}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="State *"
                        {...field}
                        error={!!errors.state}
                        helperText={errors.state?.message}
                      />
                    )}
                  />
                </Box>
              </Grid>
              <Grid container item xs gap={3} flexDirection="column">
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Primary Email *"
                      {...field}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
                <Controller
                  name="secondaryEmail"
                  control={control}
                  render={({ field }) => (
                    <TextField label="Secondary Email" {...field} />
                  )}
                />
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Cell"
                      {...field}
                      InputProps={{
                        // eslint-disable-next-line
                        inputComponent: FormatPhoneInput as any
                      }}
                    />
                  )}
                />
                <Controller
                  name="altPhone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Alt Phone"
                      {...field}
                      InputProps={{
                        // eslint-disable-next-line
                        inputComponent: FormatPhoneInput as any
                      }}
                    />
                  )}
                />
                <Controller
                  name="birthDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Birth Date"
                      openTo="year"
                      views={['year', 'month', 'day']}
                      {...field}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs>
              <Box sx={{ ...dashBorder }}>
                <Typography variant="h6" color="grey.900" mb={2}>
                  Account Settings
                </Typography>
                <Stack gap={3}>
                  <Controller
                    name="accountType"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        id="select"
                        select
                        label="Account Type *"
                        {...field}
                        error={!!errors.accountType}
                        helperText={errors.accountType?.message}
                      >
                        <MenuItem value={'W2'}>W2</MenuItem>
                        <MenuItem value={'_1099'}>1099</MenuItem>
                      </TextField>
                    )}
                  />
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        id="select"
                        select
                        label="Status *"
                        {...field}
                        error={!!errors.status}
                        helperText={errors.status?.message}
                      >
                        {Object.keys(TalentStatus).map((status) => (
                          <MenuItem key={status} value={status}>
                            {TalentStatus[status as keyof typeof TalentStatus]}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  <Controller
                    name="company"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        onChange={(event, item) => {
                          onChange(item);
                        }}
                        value={value}
                        options={companies}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        filterSelectedOptions
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Company *"
                            error={!!errors.company}
                            helperText={errors.company?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Stack>
              </Box>
            </Grid>
            <Grid item xs>
              <Box sx={{ ...dashBorder, height: '100%' }}>
                <Typography variant="h6" color="grey.900" mb={2}>
                  Payroll Information
                </Typography>
                <Controller
                  name="awPayrollId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="AW Payroll ID *"
                      {...field}
                      error={!!errors.awPayrollId}
                      helperText={errors.awPayrollId?.message}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Button type="submit" sx={{ width: 150 }}>
              Save
            </Button>
          </Box>
        </form>
      </DetailsCard>
      <DefaultModal open={successModal} handleClose={closeSuccessModal}>
        <Box sx={{ px: 6, pb: 6 }}>
          <Box textAlign="center" mb={3}>
            <img src={IconSuccess} alt="success" />
          </Box>
          <Typography variant="h3" textAlign="center" color="grey.900" mb={2}>
            Success!
          </Typography>
          <Typography textAlign="center">
            {id
              ? 'You have successfully updated Talent'
              : 'You have successfully added new Talent'}
          </Typography>
        </Box>
        <DialogActions sx={{ justifyContent: 'center', p: 3 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(ADMINISTRATION_TALENT)}
            sx={{ mr: 4 }}
          >
            View All Talents
          </Button>
          <Button onClick={closeSuccessModal}>Create New Talent</Button>
        </DialogActions>
      </DefaultModal>
    </>
  );
};

export default CreateTalent;
